import { useEffect, useRef, useState } from 'react';
import { palette } from '../../styles/tokens';
import { Card } from '../common/Card';
import { AddressAutocomplete } from '../common/AddressAutocomplete';
import type { AddressResult } from '../common/AddressAutocomplete';
import { PostalCodeInput } from '../common/PostalCodeInput';
import type { Business } from '../../types';

interface Props {
  business: Business;
  onSave: (updates: Partial<Business>) => Promise<boolean>;
}

const TIMEZONES = [
  { value: 'America/Toronto',   label: 'Eastern Time (ET)' },
  { value: 'America/Vancouver', label: 'Pacific Time (PT)' },
  { value: 'America/Edmonton',  label: 'Mountain Time (MT)' },
  { value: 'America/Winnipeg',  label: 'Central Time (CT)' },
  { value: 'America/Halifax',   label: 'Atlantic Time (AT)' },
  { value: 'America/St_Johns',  label: 'Newfoundland Time (NT)' },
];

const LABEL: React.CSSProperties = { fontSize: 13, color: palette.g500, padding: '10px 0', width: 140, flexShrink: 0 };
const VALUE: React.CSSProperties = { fontSize: 13.5, fontWeight: 500, color: palette.g800, padding: '10px 0', flex: 1 };
const INPUT: React.CSSProperties = {
  width: '100%', padding: '7px 10px', borderRadius: 6,
  border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800,
  fontFamily: 'inherit', outline: 'none',
};
const ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', borderBottom: `1px solid ${palette.g100}` };

export function BusinessProfile({ business, onSave }: Props) {
  const [editing, setEditing] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  // captured per Rule #33; businesses table has no place_id column today.
  const [placeId, setPlaceId] = useState('');
  void placeId;
  useEffect(() => { if (editing) nameRef.current?.focus(); }, [editing]);
  const [form, setForm] = useState({
    name: business.name, address: business.address ?? '', city: business.city ?? '',
    province: business.province ?? '', postal_code: business.postal_code ?? '',
    country: business.country ?? '',
    latitude: business.latitude ?? null as number | null,
    longitude: business.longitude ?? null as number | null,
    phone: business.phone ?? '', email: business.email ?? '',
    timezone: business.timezone,
  });

  const startEdit = () => { setForm({
    name: business.name, address: business.address ?? '', city: business.city ?? '',
    province: business.province ?? '', postal_code: business.postal_code ?? '',
    country: business.country ?? '',
    latitude: business.latitude ?? null,
    longitude: business.longitude ?? null,
    phone: business.phone ?? '', email: business.email ?? '',
    timezone: business.timezone,
  }); setEditing(true); };

  const save = async () => {
    await onSave({
      name: form.name, address: form.address || null, city: form.city || null,
      province: form.province || null, postal_code: form.postal_code || null,
      country: form.country || null,
      latitude: form.latitude,
      longitude: form.longitude,
      phone: form.phone || null, email: form.email || null,
      timezone: form.timezone,
    } as Partial<Business>);
    setEditing(false);
  };

  const set = (key: string, val: string) => setForm(f => ({ ...f, [key]: val }));

  if (!editing) {
    const rows: [string, string][] = [
      ['Business name', business.name],
      ['Industry', business.industry ?? '—'],
      ['Address', business.address ?? '—'],
      ['City / Province', `${business.city ?? '—'}, ${business.province ?? '—'}`],
      ['Postal code', business.postal_code ?? '—'],
      ['Phone', business.phone ?? '—'],
      ['Email', business.email ?? '—'],
      ['Timezone', business.timezone],
      ['Slug', business.slug],
    ];
    return (
      <Card title="Business Profile" actionLabel="Edit" onAction={startEdit}>
        {rows.map(([label, value], i) => (
          <div key={i} style={{ ...ROW, borderBottom: i === rows.length - 1 ? 'none' : ROW.borderBottom }}>
            <div style={LABEL}>{label}</div>
            <div style={VALUE}>{value}</div>
          </div>
        ))}
      </Card>
    );
  }

  return (
    <Card title="Business Profile">
      <div style={ROW}><div style={LABEL}>Business name</div><div style={{ flex: 1, padding: '6px 0' }}><input ref={nameRef} style={INPUT} value={form.name} onChange={e => set('name', e.target.value)} /></div></div>
      <div style={ROW}><div style={LABEL}>Industry</div><div style={{ ...VALUE, color: palette.g400 }}>{business.industry ?? '—'} (set during onboarding)</div></div>
      <div style={ROW}><div style={LABEL}>Address</div><div style={{ flex: 1, padding: '6px 0' }}>
        <AddressAutocomplete
          initialValue={form.address}
          onSelect={(r: AddressResult) => {
            setForm(f => ({
              ...f,
              address: r.address,
              city: r.city || f.city,
              province: r.province || f.province,
              postal_code: r.postalCode || f.postal_code,
              country: r.country || f.country,
              latitude: r.latitude,
              longitude: r.longitude,
            }));
            if (r.placeId) setPlaceId(r.placeId);
          }}
        />
      </div></div>
      <div style={{ display: 'flex', borderBottom: `1px solid ${palette.g100}` }}>
        <div style={LABEL}>City</div>
        <div style={{ flex: 1, padding: '6px 4px 6px 0' }}><input style={INPUT} value={form.city} onChange={e => set('city', e.target.value)} /></div>
        <div style={{ ...LABEL, width: 80 }}>Province</div>
        <div style={{ flex: 0, padding: '6px 0', width: 80 }}><input style={{ ...INPUT, width: 70 }} value={form.province} onChange={e => set('province', e.target.value)} /></div>
      </div>
      <div style={ROW}><div style={LABEL}>Postal code</div><div style={{ flex: 1, padding: '6px 0' }}><PostalCodeInput value={form.postal_code} onChange={v => set('postal_code', v)} style={{ maxWidth: 140 }} ariaLabel="Postal code" /></div></div>
      <div style={ROW}><div style={LABEL}>Phone</div><div style={{ flex: 1, padding: '6px 0' }}><input style={INPUT} value={form.phone} onChange={e => set('phone', e.target.value)} /></div></div>
      <div style={ROW}><div style={LABEL}>Email</div><div style={{ flex: 1, padding: '6px 0' }}><input style={INPUT} value={form.email} onChange={e => set('email', e.target.value)} /></div></div>
      <div style={ROW}><div style={LABEL}>HST Number</div><div style={{ ...VALUE, color: palette.g400 }}>Configured in Money → Settings → Taxes.</div></div>
      <div style={{ ...ROW, borderBottom: 'none' }}><div style={LABEL}>Timezone</div><div style={{ flex: 1, padding: '6px 0' }}>
        <select style={{ ...INPUT, maxWidth: 240 }} value={form.timezone} onChange={e => set('timezone', e.target.value)}>
          {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
        </select>
      </div></div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={() => setEditing(false)} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 500, color: palette.g500, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={save} style={{ padding: '7px 20px', borderRadius: 6, border: 'none', background: palette.accent, color: palette.white, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
      </div>
    </Card>
  );
}
